// @ts-nocheck
/**
 * Deterministic renderers for every tool's JSON result. The agent does
 * NOT narrate tool output anymore — the UI does, by inspecting the
 * structured payload directly. This keeps numbers, names, and statuses
 * authoritative (no LLM transcription errors).
 */
import React, { useMemo, useState } from 'react';
import { CheckCircle, XCircle, FileSpreadsheet, ChevronDown, ChevronRight, Layers, Filter } from 'lucide-react';
import NetIncomeSparkline, { fmtCompact } from './NetIncomeSparkline';
import { getSectionStyle, groupSections } from './sectionPalette';
import { useTaxonomy } from './taxonomyContext';

const SECTION_LABELS = {
  revenue: 'Revenue',
  cogs: 'Cost of Sales',
  opex: 'Operating Expenses',
  other_income: 'Other Income',
  other_expenses: 'Other Expenses',
  depreciation: 'Depreciation',
  amortization: 'Amortization',
  interest: 'Interest',
  tax: 'Tax',
  unassigned: 'Unassigned',
};

const fmtMoney = (n) => {
  if (n == null || isNaN(n)) return '—';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
};

const fmtMoneyFull = (n) => {
  if (n == null || isNaN(n)) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
};

const ConfidencePill = ({ confidence }) => {
  if (!confidence) return null;
  const colorMap = {
    high: '#10b981',
    medium: '#f59e0b',
    low: '#ef4444',
    none: '#9ca3af',
  };
  return (
    <span
      className="conf-pill"
      style={{ background: colorMap[confidence] || '#6b7280' }}
    >
      {confidence}
    </span>
  );
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// ---------------------------------------------------------------------------
// Per-tool summarizers (one-line headline next to the tool name)
// ---------------------------------------------------------------------------

// consolidate_workbooks is a pre-agent step, and a pre-agent step card keeps
// its input but not its result across a page reload (only real tool calls
// have their results persisted). Without a result the headline falls back to
// how many workbooks went in.
const summarizeConsolidation = (result, input) => {
  if (result?.error) return 'failed';
  const statements = result?.statements;
  if (!statements) {
    const count = input?.files?.length ?? 0;
    return count > 0 ? plural(count, 'workbook') : null;
  }
  const parts = [];
  for (const [role, label] of [['pnl', 'P&L'], ['bs', 'BS']]) {
    const months = statements[role]?.month_count;
    if (months != null) parts.push(`${label} ${plural(months, 'month')}`);
  }
  const checks = result.checks || [];
  const failed = checks.filter((c) => !c.ok).length;
  if (failed > 0) {
    parts.push(`${failed} of ${plural(checks.length, 'check')} failed`);
  } else if (checks.length > 0) {
    parts.push(`${plural(checks.length, 'check')} passed`);
  }
  return parts.join(', ') || null;
};

export function summarizeToolResult(toolName, result, input) {
  if (toolName === 'consolidate_workbooks') {
    return summarizeConsolidation(result, input);
  }
  if (result == null) return null;

  switch (toolName) {
    case 'get_sheet_structure': {
      const periods = result.periods?.length ?? 0;
      const rows = result.total_rows ?? 0;
      const d = result.period_derivation;
      if (d) {
        const dropped = (d.dropped || []).reduce((s, g) => s + (g.count || 0), 0);
        const tail = dropped > 0 ? `, ${dropped} cols dropped` : '';
        return `${rows} rows, ${periods} ${d.grain} periods${tail}`;
      }
      return `${rows} rows, ${periods} periods`;
    }
    case 'find_net_income': {
      if (result.row_index == null) return 'no candidate found';
      return `row ${result.row_index} (${result.confidence})`;
    }
    case 'classify_section': {
      const n = Array.isArray(result) ? result.length : 0;
      const sec = input?.section_name ? `${input.section_name}: ` : '';
      return `${sec}${n} classifications`;
    }
    case 'detect_totals': {
      // detect_totals returns an envelope ``{detection_method, totals,
      // notice}`` since the Pass 3 fallback was added. Accept the legacy
      // flat list for backward compatibility with older sessions.
      const arr = Array.isArray(result)
        ? result
        : Array.isArray(result?.totals)
          ? result.totals
          : [];
      const n = arr.length;
      const totals = arr.filter((r) => r.is_total).length;
      const method = result?.detection_method;
      const suffix = method === 'keyword_fallback' ? ' (keyword fallback)' : '';
      return `${totals}/${n} flagged as totals${suffix}`;
    }
    case 'verify_net_profit': {
      const status = result.status || 'unknown';
      const disc = result.final_discrepancy;
      const att = result.attempts ?? 0;
      if (status === 'pass') return `reconciled (no retries)`;
      if (status === 'converged')
        return `reconciled after ${att} ${att === 1 ? 'retry' : 'retries'}`;
      if (status === 'exhausted')
        return `unreconciled: ${fmtMoney(disc)} after ${att} retries`;
      return status;
    }
    case 'read_rows': {
      const n = Array.isArray(result) ? result.length : 0;
      return `${n} rows`;
    }
    case 'run_python': {
      return result.exit_code === 0
        ? `ok (${result.execution_time_ms}ms)`
        : `exit ${result.exit_code}`;
    }
    case 'extract_pdf': {
      if (result.error) return 'failed';
      const rows = result.row_count ?? 0;
      const periods = result.periods?.length ?? 0;
      const tie = result.tieout?.status;
      const tieText =
        tie === 'pass'
          ? 'totals tie'
          : tie === 'failed'
            ? 'totals do not tie'
            : 'no totals to check';
      return `${result.statement || 'statement'} (pages ${result.pages}): ${rows} rows, ${periods} periods, ${tieText}`;
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Per-tool full renderers (expanded card body)
// ---------------------------------------------------------------------------

export function renderToolResult(toolName, result, input) {
  if (result == null) return null;
  if (result.error) {
    return <pre className="tool-detail-error">{String(result.error)}</pre>;
  }

  switch (toolName) {
    case 'get_sheet_structure':
      return <SheetStructureCard data={result} />;
    case 'find_net_income':
      return <NetIncomeCard data={result} />;
    case 'classify_section':
      return <ClassificationsTable data={result} input={input} />;
    case 'detect_totals':
      return <TotalsTable data={result} />;
    case 'verify_net_profit':
      return <VerifyResultCard data={result} />;
    case 'read_rows':
      return <RowsTable data={result} />;
    case 'run_python':
      return <PythonResultCard data={result} />;
    case 'consolidate_workbooks':
      return <ConsolidationCard data={result} />;
    default:
      return (
        <pre className="tool-detail-raw">
          {JSON.stringify(result, null, 2)}
        </pre>
      );
  }
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// SheetStructureCard
//
// Hero-sparkline redesign. The card is composed of:
//   1. File header with filename + section-coverage chips
//   2. Four stat tiles (rows · periods · sections · formula coverage)
//   3. Periods range one-liner with a collapsible roster
//   4. Net Income hero block — sparkline + min/max/avg/total
//   5. Sections list, grouped by name, color-coded by section family
//
// Lifts the most informative number (Net Income reconciled across N
// periods) above the technical metadata. Periods and per-period values
// are tucked behind disclosures so the card is scannable.
// ---------------------------------------------------------------------------

const SheetStructureCard = ({ data }) => {
  const periods = data.periods || [];
  const sections = data.sections || [];
  const ni = data.net_income_candidate;
  const formulaPct = Math.round((data.formula_coverage || 0) * 100);
  const sortedPeriods = useMemo(
    () => [...periods].sort((a, b) => (a.period_index ?? 0) - (b.period_index ?? 0)),
    [periods]
  );
  const firstPeriod = sortedPeriods[0]?.display_name;
  const lastPeriod = sortedPeriods[sortedPeriods.length - 1]?.display_name;
  const sectionGroups = useMemo(() => groupSections(sections), [sections]);

  return (
    <div className="ssc">
      {/* Filename header */}
      <div className="ssc-fileline">
        <FileSpreadsheet size={16} />
        <span className="ssc-filename" title={data.filename}>{data.filename}</span>
      </div>

      {/* Asymmetric hero stats */}
      <HeroStats
        periods={periods.length}
        rows={data.total_rows}
        sections={sections.length}
        formulaPct={formulaPct}
      />

      {/* Period range */}
      {periods.length > 0 && (
        <PeriodRange
          first={firstPeriod}
          last={lastPeriod}
          count={periods.length}
          periods={sortedPeriods}
          headerRange={data.header_range}
          dataRange={data.data_range}
        />
      )}

      {/* Period derivation — how the raw columns were reduced + transformed
          (squeeze/cube path only; absent on ordinary sheets) */}
      {data.period_derivation && (
        <PeriodDerivationBlock derivation={data.period_derivation} />
      )}

      {/* Net Income hero */}
      {ni && <NetIncomeHero ni={ni} periods={sortedPeriods} />}

      {/* Sections — grouped table */}
      {sectionGroups.length > 0 && (
        <div className="ssc-sections">
          <div className="ssc-subhead">
            <Layers size={11} />
            Sections detected
            <span className="ssc-subhead-count">{sections.length}</span>
          </div>
          <SectionsTable groups={sectionGroups} />
        </div>
      )}
    </div>
  );
};

// ---- Sub-components for SheetStructureCard ---------------------------------

// Hero stats — four equal-weighted stat columns laid out horizontally,
// separated by a coral accent rail (rendered via CSS pseudo-elements).
// Period count leads visually as it drives the mapping shape, but all
// four read as a single row.
const HeroStats = ({ periods, rows, sections, formulaPct }) => (
  <dl className="ssc-hero">
    <div className="ssc-stat">
      <dd className="ssc-stat-number">{periods ?? '—'}</dd>
      <dt className="ssc-stat-label">{periods === 1 ? 'period' : 'periods'}</dt>
    </div>
    <div className="ssc-stat">
      <dd className="ssc-stat-number">{rows ?? '—'}</dd>
      <dt className="ssc-stat-label">rows</dt>
    </div>
    <div className="ssc-stat">
      <dd className="ssc-stat-number">{sections ?? '—'}</dd>
      <dt className="ssc-stat-label">{sections === 1 ? 'section' : 'sections'}</dt>
    </div>
    <div className="ssc-stat">
      <dd className="ssc-stat-number">{formulaPct}%</dd>
      <dt className="ssc-stat-label">formula coverage</dt>
    </div>
  </dl>
);

const PeriodRange = ({ first, last, count, periods, headerRange, dataRange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="ssc-periods">
      <button
        type="button"
        className="ssc-periods-summary"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="ssc-periods-icon">
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </span>
        <span className="ssc-periods-label">Periods</span>
        <span className="ssc-periods-range">
          {first} <span className="ssc-arrow">→</span> {last}
        </span>
        <span className="ssc-periods-meta">
          {count} {count === 1 ? 'period' : 'periods'}
        </span>
      </button>
      {open && (
        <div className="ssc-periods-detail">
          <div className="ssc-periods-tablebar">
            <span><strong>Header:</strong> rows {headerRange?.[0]}–{headerRange?.[1]}</span>
            <span><strong>Data:</strong> rows {dataRange?.[0]}–{dataRange?.[1]}</span>
          </div>
          {/* Horizontal scrolling strip — shows a few cells, drag/scroll
              to reveal the rest. Far cleaner than a wall-of-grid when N
              is large (this can hit 71+ periods on monthly files). */}
          <div className="ssc-period-strip" role="list">
            {periods.map((p) => (
              <div key={p.period_index} className="ssc-period-cell" role="listitem">
                <span className="ssc-period-name">{p.display_name}</span>
                <span className="ssc-period-col">col {p.column_index}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Period derivation — the deterministic audit note explaining how the squeeze
// reduced the sheet's raw period columns to the final set (what it dropped and
// why) and the value transform applied (YTD de-accumulation / annual collapse).
// Sourced entirely from the backend's column classifications, not LLM narration.
const PeriodDerivationBlock = ({ derivation }) => {
  const d = derivation;
  if (!d) return null;
  return (
    <div className="ssc-deriv">
      <div className="ssc-subhead">
        <Filter size={11} />
        Period derivation
        <span className="ssc-subhead-count">
          {d.source_columns} → {d.kept_periods}
        </span>
      </div>
      {d.transform && <div className="ssc-deriv-transform">{d.transform}</div>}
      {d.dropped?.length > 0 && (
        <ul className="ssc-deriv-list">
          {d.dropped.map((g, i) => (
            <li key={i} className="ssc-deriv-row">
              <span className="ssc-deriv-count">−{g.count}</span>
              <span className="ssc-deriv-reason">{g.reason}</span>
              {g.examples?.length > 0 && (
                <span className="ssc-deriv-eg">e.g. {g.examples.join(', ')}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const NetIncomeHero = ({ ni, periods }) => {
  const [showValues, setShowValues] = useState(false);

  if (ni.row_index == null) {
    return (
      <div className="ssc-ni ssc-ni-empty">
        <div className="ssc-ni-empty-row">
          <span className="ssc-ni-empty-icon">⚠</span>
          <span className="ssc-ni-empty-title">No Net Income candidate found</span>
          <ConfidencePill confidence={ni.confidence || 'none'} />
        </div>
        {ni.reason_no_match && (
          <div className="ssc-ni-empty-reason">{ni.reason_no_match}</div>
        )}
        {ni.runner_ups?.length > 0 && (
          <div className="ssc-ni-runners">
            <span className="ssc-ni-runners-label">Runner-ups:</span>
            {ni.runner_ups.map((r) => (
              <span key={r.row_index} className="ssc-ni-runner">
                row {r.row_index} · {r.account_name}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // value_by_period is keyed by column-index strings. Build an ordered
  // series that follows the periods' temporal order so the sparkline
  // reads left→right as time progresses.
  const series = useMemo(() => {
    const v = ni.value_by_period || {};
    return periods
      .map((p) => ({
        columnIndex: p.column_index,
        value: Number(v[String(p.column_index)] ?? v[p.column_index] ?? 0),
      }))
      .filter((s) => Number.isFinite(s.value));
  }, [periods, ni.value_by_period]);

  const nums = series.map((s) => s.value);
  const minV = nums.length ? Math.min(...nums) : null;
  const maxV = nums.length ? Math.max(...nums) : null;
  const total = nums.reduce((a, b) => a + b, 0);
  const avg = nums.length ? total / nums.length : null;

  return (
    <div className="ssc-ni">
      <div className="ssc-ni-head">
        <div className="ssc-ni-headline">
          <span className="ssc-ni-eyebrow">Net Income</span>
          <span className="ssc-ni-meta">row {ni.row_index}</span>
        </div>
        <ConfidencePill confidence={ni.confidence} />
      </div>

      {series.length > 0 && (
        <div className="ssc-ni-body">
          <div className="ssc-ni-chart">
            <NetIncomeSparkline series={series} periods={periods} />
          </div>
          <KpiBlock
            min={minV}
            max={maxV}
            avg={avg}
            total={total}
          />
        </div>
      )}

      {series.length > 0 && (
        <button
          type="button"
          className="ssc-ni-disclosure"
          onClick={() => setShowValues((v) => !v)}
          aria-expanded={showValues}
        >
          {showValues ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {showValues ? 'Hide' : 'Show'} all {series.length} period values
        </button>
      )}
      {showValues && (
        <div className="ssc-ni-values">
          {series.map((s, i) => {
            const label = periods[i]?.display_name || `col ${s.columnIndex}`;
            const isNeg = s.value < 0;
            return (
              <div key={s.columnIndex} className={`ssc-ni-value ${isNeg ? 'neg' : 'pos'}`}>
                <span className="ssc-ni-value-label">{label}</span>
                <span className="ssc-ni-value-amount">{fmtCompact(s.value)}</span>
              </div>
            );
          })}
        </div>
      )}

      {ni.evidence?.length > 0 && (
        <details className="ssc-ni-evidence">
          <summary>Evidence</summary>
          <ul>
            {ni.evidence.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
};

// Right-rail KPI block — labels left, values right-aligned, hairline
// above the total row to set it apart as the summary. Renders next to
// the sparkline so the chart and KPIs read as one composition.
const KpiBlock = ({ min, max, avg, total }) => {
  const cls = (n) => (n == null ? '' : n < 0 ? 'neg' : 'pos');
  return (
    <dl className="ssc-kpis">
      <div className="ssc-kpi">
        <dt>Min</dt>
        <dd className={cls(min)}>{fmtCompact(min)}</dd>
      </div>
      <div className="ssc-kpi">
        <dt>Max</dt>
        <dd className={cls(max)}>{fmtCompact(max)}</dd>
      </div>
      <div className="ssc-kpi">
        <dt>Avg</dt>
        <dd className={cls(avg)}>{fmtCompact(avg)}</dd>
      </div>
      <div className="ssc-kpi ssc-kpi-total">
        <dt>Total</dt>
        <dd className={cls(total)}>{fmtCompact(total)}</dd>
      </div>
    </dl>
  );
};

// Grouped sections table — reads like a financial schedule. The section
// name appears once on the left with a colored identity dot; each
// underlying block becomes its own range row; a subtotal row closes
// each section. The leftmost cell uses rowspan to span the section's
// block rows + the subtotal row.
const SectionsTable = ({ groups }) => (
  <table className="ssc-sections-table">
    <colgroup>
      <col className="col-name" />
      <col className="col-range" />
      <col className="col-rows" />
    </colgroup>
    <thead>
      <tr>
        <th>Name</th>
        <th>Range</th>
        <th className="num">Rows</th>
      </tr>
    </thead>
    <tbody>
      {groups.map((g, gi) => {
        const style = getSectionStyle(g.name);
        const span = g.ranges.length + 1; // ranges + subtotal row
        return (
          <React.Fragment key={g.name}>
            {g.ranges.map((r, i) => {
              const rowCount = Math.max(0, r.end_row - r.start_row + 1);
              return (
                <tr
                  key={i}
                  className={`ssc-sec-row ${i === 0 ? 'first' : 'cont'}`}
                  style={{ borderLeftColor: style.dot }}
                >
                  {i === 0 && (
                    <td
                      className="ssc-sec-name"
                      rowSpan={span}
                      style={{ color: style.fg }}
                    >
                      <span className="ssc-sec-dot" style={{ background: style.dot }} />
                      {g.name}
                    </td>
                  )}
                  <td className="ssc-sec-range">rows {r.start_row}–{r.end_row}</td>
                  <td className="num ssc-sec-rownum">{rowCount}</td>
                </tr>
              );
            })}
            <tr className="ssc-sec-subtotal" style={{ borderLeftColor: style.dot }}>
              <td className="ssc-sec-subtotal-label">subtotal</td>
              <td className="num ssc-sec-subtotal-num" style={{ color: style.fg }}>{g.rows}</td>
            </tr>
          </React.Fragment>
        );
      })}
    </tbody>
  </table>
);

const NetIncomeCard = ({ data, compact }) => {
  if (data.row_index == null) {
    return (
      <div className="result-card">
        <div className="result-row">
          <span className="muted">No candidate found.</span>
          <ConfidencePill confidence={data.confidence} />
        </div>
        {data.reason_no_match && (
          <div className="muted-small">{data.reason_no_match}</div>
        )}
        {data.runner_ups?.length > 0 && (
          <>
            <div className="result-subhead">Runner-ups</div>
            <table className="result-table">
              <tbody>
                {data.runner_ups.map((r) => (
                  <tr key={r.row_index}>
                    <td className="muted-small">row {r.row_index}</td>
                    <td>{r.account_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    );
  }

  const values = Object.entries(data.value_by_period || {});

  return (
    <div className={`result-card ${compact ? 'compact' : ''}`}>
      <div className="result-row spread">
        <div>
          <div className="strong">{data.account_name}</div>
          <div className="muted-small">
            row {data.row_index} · {data.detection_method}
          </div>
        </div>
        <ConfidencePill confidence={data.confidence} />
      </div>

      {values.length > 0 && (
        <div className="result-subhead">Value by period</div>
      )}
      {values.length > 0 && (
        <div className="chips-row">
          {values.map(([col, v]) => (
            <span key={col} className="chip">
              <span className="chip-muted">col {col}</span>
              <span className="chip-strong">{fmtMoneyFull(v)}</span>
            </span>
          ))}
        </div>
      )}

      {data.evidence?.length > 0 && (
        <>
          <div className="result-subhead">Evidence</div>
          <ul className="evidence-list">
            {data.evidence.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

const ClassificationsTable = ({ data, input }) => {
  const { badgeStyle } = useTaxonomy();
  if (!Array.isArray(data) || !data.length) {
    return <div className="muted-small">No classifications returned.</div>;
  }

  const sectionName = input?.section_name || 'All';
  const lowConf = data.filter((c) => (c.confidence ?? 0) < 0.6).length;

  return (
    <div className="result-card">
      <div className="result-row spread">
        <div className="strong">{sectionName}</div>
        <div className="muted-small">
          {data.length} rows · {lowConf} low-confidence
        </div>
      </div>
      <table className="result-table scroll classify-table">
        <thead>
          <tr>
            <th>Row</th>
            <th>Account</th>
            <th>Value</th>
            <th>Category</th>
            <th>Conf</th>
          </tr>
        </thead>
        <tbody>
          {data.map((c) => {
            const lowConfidence = (c.confidence ?? 0) < 0.6;
            const cat = c.category || 'Uncategorized';
            return (
              <tr
                key={c.row_index}
                className={lowConfidence ? 'low-conf' : ''}
              >
                <td className="muted-small">{c.row_index}</td>
                <td title={c.account_name || ''}>
                  <span className="classify-account">
                    {c.account_name || <span className="muted">—</span>}
                  </span>
                  {c.reasoning && (
                    <div className="classify-reasoning" title={c.reasoning}>
                      {c.reasoning}
                    </div>
                  )}
                </td>
                <td className="mono muted-small">
                  {c.value != null ? fmtMoney(c.value) : '—'}
                </td>
                <td>
                  <span className="cat-badge" style={badgeStyle(cat)}>
                    {cat}
                  </span>
                  {c.subcategory && c.subcategory !== cat && (
                    <div className="muted-small">{c.subcategory}</div>
                  )}
                </td>
                <td>{(c.confidence ?? 0).toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const TotalsTable = ({ data }) => {
  // detect_totals now returns ``{detection_method, totals, notice}``.
  // Older sessions / tests may still hand us a flat list — unwrap both.
  const totals = Array.isArray(data)
    ? data
    : Array.isArray(data?.totals)
      ? data.totals
      : [];
  const method = data?.detection_method;
  const notice = data?.notice;
  const showFallbackBanner = method === 'keyword_fallback';

  if (!totals.length) {
    return (
      <div>
        {showFallbackBanner && notice ? (
          <div className="result-notice warn">{notice}</div>
        ) : null}
        <div className="muted-small">No totals detected.</div>
      </div>
    );
  }
  return (
    <div className="result-card">
      {showFallbackBanner && notice ? (
        <div className="result-notice warn">{notice}</div>
      ) : null}
      <table className="result-table scroll">
        <thead>
          <tr>
            <th>Row</th>
            <th>Account</th>
            <th>Conf</th>
            <th>Evidence</th>
          </tr>
        </thead>
        <tbody>
          {totals.map((t) => (
            <tr key={t.row_index}>
              <td className="muted-small">{t.row_index}</td>
              <td className="strong">
                {t.account_name || <span className="muted">—</span>}
              </td>
              <td>{(t.confidence ?? 0).toFixed(2)}</td>
              <td className="muted-small">{t.evidence}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SECTION_SIGNS = {
  revenue: 1,
  cogs: -1,
  opex: -1,
  other_income: 1,
  other_expenses: -1,
  amortization: -1,
  depreciation: -1,
  interest: -1,
  tax: -1,
};

const fmtAccounting = (n) => {
  if (n == null || isNaN(n)) return '—';
  if (n === 0 || Math.abs(n) < 0.005) return '0.00';
  const abs = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `(${abs})` : abs;
};

const fmtSigned = (n) => {
  if (n == null || isNaN(n)) return '—';
  if (Math.abs(n) < 0.005) return '0.00';
  const prefix = n > 0 ? '+' : '';
  return `${prefix}${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const PeriodBreakdownTable = ({ breakdown }) => {
  const rollups = (breakdown.section_rollups || []).filter(
    (r) => r.section !== 'unassigned',
  );
  const unassigned = (breakdown.section_rollups || []).find(
    (r) => r.section === 'unassigned' && r.account_count > 0,
  );

  return (
    <div className="vp-period">
      <table className="vp-breakdown-table">
        <thead>
          <tr>
            <th></th>
            <th>Section</th>
            <th className="vp-num">Total</th>
            <th className="vp-num">NI Impact</th>
          </tr>
        </thead>
        <tbody>
          {rollups.map((r, i) => {
            const sign = r.sign ?? SECTION_SIGNS[r.section] ?? 0;
            const signChar = sign > 0 ? '+' : sign < 0 ? '−' : '·';
            return (
              <tr
                key={i}
                className={
                  r.contribution > 0
                    ? 'vp-row positive'
                    : r.contribution < 0
                      ? 'vp-row negative'
                      : 'vp-row'
                }
              >
                <td className="vp-sign">{signChar}</td>
                <td>
                  {SECTION_LABELS[r.section] || r.section}
                  <span className="vp-count"> ({r.account_count})</span>
                </td>
                <td className="vp-num">{fmtAccounting(r.total)}</td>
                <td className="vp-num">{fmtSigned(r.contribution)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="vp-total-row">
            <td>=</td>
            <td>Mapped Net Income</td>
            <td className="vp-num" colSpan={2}>
              {fmtAccounting(breakdown.mapped_net_income)}
            </td>
          </tr>
          {breakdown.benchmark_value != null && (
            <>
              <tr className="vp-benchmark-row">
                <td></td>
                <td>Original ({breakdown.benchmark_name || 'Net Income'})</td>
                <td className="vp-num" colSpan={2}>
                  {fmtAccounting(breakdown.benchmark_value)}
                </td>
              </tr>
              <tr
                className={`vp-disc-row ${breakdown.passes ? 'pass' : 'fail'}`}
              >
                <td></td>
                <td>
                  Discrepancy
                  <span
                    className={`vp-badge ${breakdown.passes ? 'pass' : 'fail'}`}
                  >
                    {breakdown.passes ? 'PASS' : 'FAIL'}
                  </span>
                </td>
                <td className="vp-num" colSpan={2}>
                  {fmtAccounting(breakdown.discrepancy)}
                </td>
              </tr>
            </>
          )}
        </tfoot>
      </table>

      {unassigned && (
        <div className="vp-unassigned">
          {unassigned.account_count} unassigned account
          {unassigned.account_count === 1 ? '' : 's'} totaling{' '}
          {fmtAccounting(unassigned.total)} — not included in NI
        </div>
      )}
    </div>
  );
};

const VerifyResultCard = ({ data }) => {
  const statusColors = {
    pass: '#10b981',
    converged: '#10b981',
    exhausted: '#ef4444',
    error: '#ef4444',
  };

  const periods = Array.isArray(data.period_breakdowns)
    ? data.period_breakdowns
    : [];
  const passCount = periods.filter((p) => p.passes).length;
  const failCount = periods.length - passCount;

  const [selectedIdx, setSelectedIdx] = useState(() => {
    const firstFail = periods.findIndex((p) => !p.passes);
    return firstFail >= 0 ? firstFail : 0;
  });

  const current = periods[selectedIdx];

  return (
    <div className="result-card vp-card">
      <div className="result-row spread">
        <span
          className="status-pill"
          style={{ background: statusColors[data.status] || '#6b7280' }}
        >
          {data.status}
        </span>
        <div className="vp-status-counts">
          {passCount > 0 && (
            <span className="vp-pass-count">
              <CheckCircle size={11} /> {passCount} pass
            </span>
          )}
          {failCount > 0 && (
            <span className="vp-fail-count">
              <XCircle size={11} /> {failCount} fail
            </span>
          )}
          <span className="muted-small">
            {data.attempts ?? 0} silent {data.attempts === 1 ? 'retry' : 'retries'}
          </span>
        </div>
      </div>

      <div className="result-grid">
        <Field
          label="Final discrepancy"
          value={fmtMoneyFull(data.final_discrepancy)}
        />
        <Field label="Corrections" value={data.corrections?.length ?? 0} />
        <Field label="Periods checked" value={periods.length || '—'} />
      </div>

      {data.rationale && <div className="muted-small">{data.rationale}</div>}

      {periods.length > 0 && (
        <>
          <div className="result-subhead">Per-period breakdown</div>
          <div className="vp-layout">
            {periods.length > 1 && (
              <div className="vp-period-rail">
                {periods.map((p, idx) => (
                  <button
                    key={p.period_index}
                    type="button"
                    className={`vp-period-tab ${
                      idx === selectedIdx ? 'active' : ''
                    } ${p.passes ? 'pass' : 'fail'}`}
                    onClick={() => setSelectedIdx(idx)}
                  >
                    {p.period_display}
                    {p.passes ? (
                      <CheckCircle size={10} />
                    ) : (
                      <XCircle size={10} />
                    )}
                  </button>
                ))}
              </div>
            )}
            {current && <PeriodBreakdownTable breakdown={current} />}
          </div>
        </>
      )}

      {data.corrections?.length > 0 && (
        <>
          <div className="result-subhead">Corrections applied silently</div>
          <table className="result-table scroll">
            <thead>
              <tr>
                <th>Row</th>
                <th>Account</th>
                <th>From → To</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {data.corrections.map((c, i) => (
                <tr key={i}>
                  <td className="muted-small">{c.row_index}</td>
                  <td>{c.original_name}</td>
                  <td>
                    <span className="muted-small">{c.old_category}</span>
                    {' → '}
                    <span className="strong">{c.new_category}</span>
                  </td>
                  <td className="muted-small">{c.reasoning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

const RowsTable = ({ data }) => {
  if (!Array.isArray(data) || !data.length) {
    return <div className="muted-small">No rows.</div>;
  }
  return (
    <div className="result-card">
      <table className="result-table scroll">
        <thead>
          <tr>
            <th>Row</th>
            <th>Account</th>
            <th>Indent</th>
            <th>Formula</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r) => (
            <tr key={r.row_index}>
              <td className="muted-small">{r.row_index}</td>
              <td>{r.account_name}</td>
              <td className="muted-small">{r.indentation_level}</td>
              <td className="muted-small mono">{r.formula || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PythonResultCard = ({ data }) => (
  <div className="result-card">
    <div className="result-row spread">
      <span
        className="status-pill"
        style={{ background: data.exit_code === 0 ? '#10b981' : '#ef4444' }}
      >
        exit {data.exit_code}
      </span>
      <div className="muted-small">{data.execution_time_ms}ms</div>
    </div>
    {data.stdout && (
      <>
        <div className="result-subhead">stdout</div>
        <pre className="result-pre">{data.stdout}</pre>
      </>
    )}
    {data.stderr && (
      <>
        <div className="result-subhead">stderr</div>
        <pre className="result-pre err">{data.stderr}</pre>
      </>
    )}
  </div>
);

const CONSOLIDATED_STATEMENTS = [
  ['pnl', 'Income statement'],
  ['bs', 'Balance sheet'],
];

const monthRange = (months) => {
  const [first, last] = Array.isArray(months) ? months : [];
  if (first && last && first !== last) return `${first} to ${last}`;
  return first || last || null;
};

// consolidate_workbooks: the Databook pre-agent step that reads every
// uploaded workbook and assembles one income statement and one balance
// sheet. Per statement: the months covered, the row count and the sheets it
// was built from; then the step's own checks and warnings. Every field is
// optional so a partial payload still renders.
const ConsolidationCard = ({ data }) => {
  const statements = data.statements || {};
  const checks = data.checks || [];
  const warnings = data.warnings || [];

  return (
    <div className="result-card">
      {(data.entity || data.fiscal_year_end) && (
        <div className="result-row spread">
          <div className="strong">{data.entity}</div>
          {data.fiscal_year_end && (
            <div className="muted-small">FY ends {data.fiscal_year_end}</div>
          )}
        </div>
      )}

      {CONSOLIDATED_STATEMENTS.map(([role, label]) => {
        const statement = statements[role];
        if (!statement) return null;
        const sources = statement.sources || [];
        const range = monthRange(statement.months);
        const size = [
          statement.month_count != null && plural(statement.month_count, 'month'),
          statement.rows != null && plural(statement.rows, 'row'),
        ].filter(Boolean);
        return (
          <React.Fragment key={role}>
            <div className="result-subhead">{label}</div>
            <div className="result-row">
              {range && <span className="strong">{range}</span>}
              <span className="muted-small">{size.join(' · ')}</span>
            </div>
            {sources.length > 0 && (
              <table className="result-table scroll">
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Sheet</th>
                    <th>Months</th>
                    <th>Rows</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map((source, i) => (
                    <tr key={i}>
                      <td>{source.filename}</td>
                      <td className="muted-small">{source.sheet}</td>
                      <td className="muted-small">{monthRange(source.months)}</td>
                      <td className="muted-small">{source.rows}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </React.Fragment>
        );
      })}

      {checks.length > 0 && (
        <>
          <div className="result-subhead">Checks</div>
          {checks.map((check, i) => (
            <div key={i} className="result-row">
              <span className={check.ok ? 'vp-pass-count' : 'vp-fail-count'}>
                {check.ok ? <CheckCircle size={11} /> : <XCircle size={11} />}
                {check.name}
              </span>
              {check.detail && (
                <span className="muted-small">{check.detail}</span>
              )}
            </div>
          ))}
        </>
      )}

      {warnings.length > 0 && (
        <>
          <div className="result-subhead">Warnings</div>
          {warnings.map((warning, i) => (
            <div key={i} className="result-notice warn">
              {warning}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

const Field = ({ label, value }) => (
  <div className="field">
    <div className="field-label">{label}</div>
    <div className="field-value">{value}</div>
  </div>
);
